import React, { useState, useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { Delete } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import Card from '@/components/Card/Card';
import CardBody from '@/components/Card/CardBody';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import Button from '@/components/CustomButtons/Button';
import CustomInput from '@/components/CustomInput/CustomInput';
import { useStyles } from '@/styles/templateInputStyles';

export default function CustomPaymentTypeMappings({
  formValues,
  setFormValues,
  isUpdating,
  customPaymentTypesForm,
  dbColumns,
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [expanded, setExpanded] = useState({});

  const handleAccordionChange = (id) => (event, isExpanded) => {
    setExpanded((prev) => ({ ...prev, [id]: isExpanded }));
  };

  useEffect(() => {
    const selectedIds = formValues.custom_payment_type_ids || [];
    let updated = false;
    const newMappings = { ...(formValues.custom_payment_types_mappings || {}) };

    selectedIds.forEach((id) => {
      if (!newMappings[id]) {
        updated = true;
        const emptyMapping = Object.keys(
          formValues.regular_data_mapping?.structure || {},
        ).reduce((acc, key) => {
          acc[key] = '';
          return acc;
        }, {});

        newMappings[id] = {
          structure: emptyMapping,
          exceptions: [
            {
              subStatus: 'Payee Exception',
              columnName: 'business_payee_name',
              contains: [],
              operation: '=',
            },
          ],
        };
      }
    });

    if (updated) {
      setFormValues((prev) => ({
        ...prev,
        custom_payment_types_mappings: newMappings,
      }));
    }
  }, [formValues.custom_payment_type_ids, formValues.regular_data_mapping]);

  const handleMappingChange = (id, key, value) => {
    setFormValues((prev) => {
      return {
        ...prev,
        custom_payment_types_mappings: {
          ...prev.custom_payment_types_mappings,
          [id]: {
            ...prev.custom_payment_types_mappings[id],
            structure: {
              ...prev.custom_payment_types_mappings[id].structure,
              [key]: value,
            },
          },
        },
      };
    });
  };

  const handleOperationChange = (id, value) => {
    setFormValues((prev) => {
      const currentExceptions =
        prev.custom_payment_types_mappings[id]?.exceptions || [];
      const updatedExceptions = currentExceptions.map((ex) => ({
        ...ex,
        operation: value,
      }));

      return {
        ...prev,
        custom_payment_types_mappings: {
          ...prev.custom_payment_types_mappings,
          [id]: {
            ...prev.custom_payment_types_mappings[id],
            exceptions: updatedExceptions,
          },
        },
      };
    });
  };

  const addException = (id) => {
    setFormValues((prev) => {
      const currentExceptions =
        prev.custom_payment_types_mappings[id]?.exceptions || [];
      const firstException = currentExceptions[0] || {
        subStatus: 'Payee Exception',
        columnName: 'business_payee_name',
        contains: [],
        operation: '=',
      };

      const updatedExceptions = [
        {
          ...firstException,
          contains: [...(firstException.contains || []), ''],
        },
      ];

      return {
        ...prev,
        custom_payment_types_mappings: {
          ...prev.custom_payment_types_mappings,
          [id]: {
            ...prev.custom_payment_types_mappings[id],
            exceptions: updatedExceptions,
          },
        },
      };
    });
  };

  const removeException = (id, index) => {
    setFormValues((prev) => {
      const currentExceptions =
        prev.custom_payment_types_mappings[id]?.exceptions || [];
      const firstException = currentExceptions[0];
      if (!firstException) return prev;

      const updatedContains = firstException.contains.filter(
        (_, i) => i !== index,
      );

      return {
        ...prev,
        custom_payment_types_mappings: {
          ...prev.custom_payment_types_mappings,
          [id]: {
            ...prev.custom_payment_types_mappings[id],
            exceptions: [
              {
                ...firstException,
                contains: updatedContains,
              },
            ],
          },
        },
      };
    });
  };

  const onExceptionChange = (id, index, value) => {
    setFormValues((prev) => {
      const currentExceptions =
        prev.custom_payment_types_mappings[id]?.exceptions || [];
      const firstException = currentExceptions[0];
      if (!firstException) return prev;

      const updatedContains = firstException.contains.map((c, i) =>
        i === index ? value : c,
      );

      return {
        ...prev,
        custom_payment_types_mappings: {
          ...prev.custom_payment_types_mappings,
          [id]: {
            ...prev.custom_payment_types_mappings[id],
            exceptions: [
              {
                ...firstException,
                contains: updatedContains,
              },
            ],
          },
        },
      };
    });
  };

  const handleCopyMapping = (id) => {
    const mappingData = formValues.custom_payment_types_mappings?.[id];
    if (!mappingData) return;

    const dataToCopy = {
      isCustomPaymentMapping: true,
      data: mappingData,
    };
    navigator.clipboard.writeText(JSON.stringify(dataToCopy));
    toast.success('Mapping copied to clipboard');
  };

  const handlePaste = (id, event) => {
    try {
      const clipboardData = event.clipboardData.getData('text');
      const parsedData = JSON.parse(clipboardData);

      if (parsedData && parsedData.isCustomPaymentMapping && parsedData.data) {
        event.preventDefault();
        setFormValues((prev) => ({
          ...prev,
          custom_payment_types_mappings: {
            ...prev.custom_payment_types_mappings,
            [id]: parsedData.data,
          },
        }));
        toast.success('Mapping pasted successfully');
      }
    } catch (e) {
      console.error('Error parsing clipboard data:', e);
    }
  };

  const labelStyle = {
    fontSize: '12px',
    color: 'grey',
    cursor: 'pointer',
  };

  if (!formValues.custom_payment_type_ids?.length) return null;

  return (
    <div
      style={{
        marginTop: '15px',
      }}>
      {customPaymentTypesForm?.length &&
        formValues.custom_payment_type_ids?.map((id) => {
          const customPaymentType = customPaymentTypesForm?.find(
            (type) => type.id === id,
          );

          const typeName = customPaymentType.name;
          const identifiers = customPaymentType.identifiers || [];
          const enableExceptions =
            customPaymentType.enablePayeeExceptionWorkflow;

          const mappingData = formValues.custom_payment_types_mappings?.[id];
          const operation = mappingData?.exceptions?.[0]?.operation || '=';
          const structureKeys = dbColumns.map((col) => col.value);

          return (
            <Accordion
              key={id}
              expanded={expanded[id] || false}
              onChange={handleAccordionChange(id)}
              disabled={!isUpdating('data-mapping')}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`panel-${id}-content`}
                id={`panel-${id}-header`}>
                <Typography>
                  {typeName + ' ' + '(' + identifiers.join(', ') + ')'}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Card>
                  <CardBody>
                    <div
                      style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        color='primary'
                        size='sm'
                        onClick={() => handleCopyMapping(id)}
                        disabled={!isUpdating('data-mapping')}>
                        Copy Mapping
                      </Button>
                    </div>
                    <GridContainer style={{ justifyContent: 'space-between' }}>
                      {structureKeys.map((key) => (
                        <GridItem
                          key={key}
                          style={{ margin: '10px', minWidth: '30%' }}>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              width: '100%',
                            }}>
                            <div
                              onClick={() => {
                                navigator.clipboard.writeText(`{{${key}}}`);
                                toast.success(
                                  t('createEditDataMappings.copiedToClipboard'),
                                  {
                                    autoClose: 1000,
                                  },
                                );
                              }}
                              style={{ display: 'flex', alignItems: 'center' }}>
                              <label style={labelStyle}>
                                {' '}
                                {key} - {'{{'}
                                {key}
                                {'}}'}
                              </label>{' '}
                              <AssignmentOutlinedIcon
                                style={{
                                  color: '#3AB795',
                                  fontSize: '20px',
                                  cursor: 'copy',
                                }}
                              />
                            </div>

                            <TextField
                              fullWidth={true}
                              disabled={!isUpdating('data-mapping')}
                              variant='outlined'
                              color='primary'
                              className={classes.root}
                              placeholder={key}
                              multiline
                              onPaste={(e) => handlePaste(id, e)}
                              inputProps={{
                                style: {
                                  outlineColor: '#00bcd4',
                                  borderColor: '#00bcd4',
                                  fontSize: '14px',
                                  lineHeight: '1',
                                  width: '300px',
                                },
                              }}
                              minRows={1}
                              maxRows={1}
                              value={mappingData?.structure?.[key] || ''}
                              onChange={(e) =>
                                handleMappingChange(id, key, e.target.value)
                              }
                              name={key}
                            />
                          </div>
                        </GridItem>
                      ))}
                    </GridContainer>

                    {enableExceptions && (
                      <>
                        <GridItem xs={6} sm={6} md={6}>
                          <div className='custom-select'>
                            <FormControl variant='standard'>
                              <InputLabel id={`operation-${id}`}>
                                Business Payee Name Matching
                              </InputLabel>
                              <Select
                                labelId={`operation-${id}`}
                                value={operation}
                                onChange={(e) =>
                                  handleOperationChange(id, e.target.value)
                                }
                                disabled={!isUpdating('data-mapping')}
                                name='operation'
                                className='full-width-elem'>
                                <MenuItem value='='>
                                  Business Payee Name Below Are Exceptions
                                </MenuItem>
                                <MenuItem value='!='>
                                  Business Payee Name Below Are Not Exceptions
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </div>
                        </GridItem>

                        <GridItem xs={12} sm={12} md={12}>
                          <GridContainer>
                            {mappingData?.exceptions?.[0]?.contains?.map(
                              (exceptionValue, index) => (
                                <GridItem xs={6} sm={6} md={6} key={index}>
                                  <div
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}>
                                    <div
                                      style={{
                                        width: '80%',
                                      }}>
                                      <CustomInput
                                        type='text'
                                        labelText={`Business Payee Name`}
                                        onChange={(e) =>
                                          onExceptionChange(
                                            id,
                                            index,
                                            e.target.value,
                                          )
                                        }
                                        value={exceptionValue}
                                        name='exceptionValue'
                                        formControlProps={{
                                          fullWidth: true,
                                        }}
                                        disabled={!isUpdating('data-mapping')}
                                      />
                                    </div>
                                    <Delete
                                      onClick={() => removeException(id, index)}
                                      style={{
                                        cursor: 'pointer',
                                        marginTop: '30px',
                                      }}
                                    />
                                  </div>
                                </GridItem>
                              ),
                            )}
                          </GridContainer>
                          <GridItem
                            xs={12}
                            sm={12}
                            md={6}
                            style={{ padding: 0 }}>
                            <Button
                              color='primary'
                              disabled={!isUpdating('data-mapping')}
                              onClick={() => addException(id)}>
                              Add Business Payee Name
                            </Button>
                          </GridItem>
                        </GridItem>
                      </>
                    )}
                  </CardBody>
                </Card>
              </AccordionDetails>
            </Accordion>
          );
        })}
    </div>
  );
}
